import { makeStyles, Theme, createStyles, Grid, Typography } from '@material-ui/core';
import WorkOutlinedIcon from '@material-ui/icons/WorkOutlined';
import React from 'react';
import { RouterProps } from '../App';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { MatterTerminology } from '../MatterTerminology';
import { PageContentLayout } from '../v2/layouts/PageContentLayout';
import CommonMaterialCard from '../configurationDashboard/CommonMaterialCard';
import { AnimatedItem } from '../components/animations/AnimatedItem';
import GeneralMaterialIcon, { getFileType } from './GeneralMaterialIcon';

const SKELETON_CARD_COUNT = 8;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // GeneralMaterialIcon carries its own 1.5em sizing — shrink it to the card's 22px icon-chip size
        materialIcon: {
            display: 'flex',
            '& > svg': {
                width: 22,
                height: 22,
            },
        },
        emptyState: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            textAlign: 'center',
            padding: theme.spacing(4, 2),
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
        },
    })
);

interface GeneralMaterialPageProps extends RouterProps {
    isConvey: boolean;
}

export default function GeneralMaterialPage(props: GeneralMaterialPageProps) {

    const classes = useStyles();

    const onClickBack = () => {
        props.history.goBack();
    };

    const linkTo = (url: string) => () => {
        window.open(url, '_blank');
    };

    // tslint:disable-next-line:no-any
    const generalMaterialsQuery = useQuery<GeneralMaterialsData>(GENERAL_MATERIALS_QUERY, {
        variables: {
            isConvey: true,
        },
        fetchPolicy: 'cache-and-network',
    });

    const generalMaterials = generalMaterialsQuery.data && generalMaterialsQuery.data.generalMaterialList
        ? generalMaterialsQuery.data.generalMaterialList.generalMaterials
        : [];

    const renderContent = () => {
        if (generalMaterialsQuery.loading) {
            return (
                <Grid container={true} spacing={1} className="rv-5col-grid">
                    {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
                        <Grid key={index} item={true} xs={12} sm={6} md={4} lg={3}>
                            <CommonMaterialCard loading={true} />
                        </Grid>
                    ))}
                </Grid>
            );
        }

        if (generalMaterials.length === 0) {
            return (
                <Typography className={classes.emptyState}>
                    No knowledge bank materials are available yet.
                </Typography>
            );
        }

        return (
            <Grid container={true} spacing={1} className="rv-5col-grid">
                {generalMaterials.map((material, index) => (
                    <Grid key={material.id} item={true} xs={12} sm={6} md={4} lg={3}>
                        <AnimatedItem delay={index * 10}>
                            <CommonMaterialCard
                                // GeneralMaterialIcon opens the link itself — stop the click also
                                // reaching the card's onClick, which would open it a second time
                                icon={(
                                    <span className={classes.materialIcon} onClick={event => event.stopPropagation()}>
                                        <GeneralMaterialIcon
                                            iconKey={getFileType(material.fileName)}
                                            url={material.url}
                                        />
                                    </span>
                                )}
                                title={material.label}
                                description={material.description}
                                onClick={linkTo(material.url)}
                            />
                        </AnimatedItem>
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <PageContentLayout
            pageHeader={{
                icon: <WorkOutlinedIcon fontSize="small" />,
                title: MatterTerminology.GeneralMaterial,
                subtitle: 'Guides, documents and links to help you get the most out of RedView',
                onBack: onClickBack,
            }}
            hideToolbar={true}
        >
            {renderContent()}
        </PageContentLayout>
    );
}

export interface GeneralMaterialsData {
    generalMaterialList: GeneralMaterialList;
}

export interface GeneralMaterialList {
    generalMaterials: GeneralMaterial[];
}

export interface GeneralMaterial {
    id: number;
    type: number;
    sequence: number;
    label: string;
    fileName: null | string;
    url: string;
    isConvey: boolean;
    description: string;
    matterCategory: null | string;
    matterTypeId: number;
    matterType: null | string;
}

const GENERAL_MATERIALS_QUERY = gql`
query GeneralMaterials ($isConvey: Boolean){
    generalMaterialList(isConvey: $isConvey) {
      generalMaterials {
        id,
        type,
        sequence,
        label,
        fileName,
        url,
        isConvey,
        description,
        matterCategory,
        matterCategory,
        matterTypeId,
        matterType
      }
    }
  }
`;
